import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";
import { showErrorToast } from "../dist/Toaster";
import XLSX from "xlsx-js-style";

const StockistPOBSOBTargetQuarterly = () => {
  const tableRef = useRef(null);

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([
    { id: "All", first_name: "All", last_name: "" },
  ]);
// test
  const [selectedState, setSelectedState] = useState("All");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedEmployee, setSelectedEmployee] = useState("All");

  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [quarters, setQuarters] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const startYear = 2018;
    const allQuarters = [];

    for (let year = startYear; year <= currentYear; year++) {
      allQuarters.push(
        {
          label: `01/Jan/${year} - 31/Mar/${year}`,
          value: `Q1-${year}`,
          months: [1, 2, 3],
        },
        {
          label: `01/Apr/${year} - 30/Jun/${year}`,
          value: `Q2-${year}`,
          months: [4, 5, 6],
        },
        {
          label: `01/Jul/${year} - 30/Sep/${year}`,
          value: `Q3-${year}`,
          months: [7, 8, 9],
        },
        {
          label: `01/Oct/${year} - 31/Dec/${year}`,
          value: `Q4-${year}`,
          months: [10, 11, 12],
        }
      );
    }

    const active =
      allQuarters.find(
        (q) =>
          q.value.startsWith("Q") &&
          q.months.includes(currentMonth) &&
          q.value.endsWith(`-${currentYear}`)
      ) || allQuarters[0];

    setQuarters(allQuarters);
    setSelectedQuarter(active.value);
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        if (!mounted) return;
        setStates([
          { id: "All", name: "All" },
          ...(Array.isArray(res.data) ? res.data : []),
        ]);
        setSelectedState("All");
      } catch {
        if (!mounted) return;
        setStates([{ id: "All", name: "All" }]);
        setError("Failed to load states");
      }
    };
    fetchStates();
    return () => {
      mounted = false;
    };
  }, []);

  const fetchDivisions = useCallback(async (stateId = "All") => {
    try {
      let url = `${BASE_URL}/api/divisions`;
      if (stateId && stateId !== "All")
        url += `?state_id=${encodeURIComponent(stateId)}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      setDivisions([
        { id: "All", name: "All" },
        ...(Array.isArray(res.data) ? res.data : []),
      ]);
      setSelectedDivision("All");
    } catch {
      setDivisions([{ id: "All", name: "All" }]);
      setSelectedDivision("All");
    }
  }, []);

  useEffect(() => {
    fetchDivisions("All");
  }, [fetchDivisions]);

  const fetchEmployees = useCallback(
    async (stateId = "All", divisionId = "All") => {
      try {
        setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
        setSelectedEmployee("All");
        const params = {};
        if (stateId && stateId !== "All") params.state_id = stateId;
        if (divisionId && divisionId !== "All") {
          const division = divisions.find(
            (d) => String(d.id) === String(divisionId),
          );
          if (division && division.name) params.division = division.name;
        }
        const res = await axios.get(`${BASE_URL}/api/employees`, {
          params: Object.keys(params).length ? params : undefined,
          headers: getAuthHeaders(),
        });
        setEmployees([
          { id: "All", first_name: "All", last_name: "" },
          ...(Array.isArray(res.data) ? res.data : []),
        ]);
        setSelectedEmployee("All");
      } catch {
        setEmployees([{ id: "All", first_name: "All", last_name: "" }]);
        setSelectedEmployee("All");
      }
    },
    [divisions],
  );

  const handleStateChange = async (stateId) => {
    const id = stateId || "All";
    setSelectedState(id);
    setSelectedDivision("All");
    setSelectedEmployee("All");
    setShowTable(false);
    await fetchDivisions(id);
    fetchEmployees(id, "All");
  };

  const handleDivisionChange = (divisionId) => {
    const id = divisionId || "All";
    setSelectedDivision(id);
    setSelectedEmployee("All");
    setShowTable(false);
    fetchEmployees(selectedState || "All", id);
  };

  const handleGoClick = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setTableData([]);
      setShowTable(false);
      const selected = quarters.find((q) => q.value === selectedQuarter);
      if (!selected) {
        setError("Please select a valid quarter.");
        setIsLoading(false);
        return;
      }
      const divisionObj = divisions.find(
        (d) => String(d.id) === String(selectedDivision),
      );
      const divisionName =
        divisionObj && divisionObj.name !== "All"
          ? divisionObj.name
          : undefined;
      const params = {
        ...(selectedState && selectedState !== "All"
          ? { state_id: selectedState }
          : {}),
        ...(divisionName ? { division_id: divisionName } : {}),
        year: parseInt(selected.value.split("-")[1], 10),
        months: selected.months.join(","),
        ...(selectedEmployee && selectedEmployee !== "All"
          ? { employee_id: selectedEmployee }
          : {}),
      };
      const response = await axios.get(
        `${BASE_URL}/api/stockist/quarterly-target`,
        {
          params,
          headers: getAuthHeaders(),
        },
      );
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];
      setTableData(data);
      setShowTable(true);
    } catch {
      setError("Failed to fetch report. See console for details.");
      setTableData([]);
      setShowTable(false);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatNumber = (num) =>
    num || num === 0 ? Number(num).toLocaleString() : "0";

  const handlePrint = () => {
    if (!tableRef.current) return;

    const printContents = tableRef.current.innerHTML;
    const title = "Stockist POB vs SOB Quarterly Report";

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial; padding: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #000; padding: 6px; }
          th { background: #eee; }
        </style>
      </head>
      <body>
        <h2>${title}</h2>
        ${printContents}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

const handleExport = async () => {
  try {

    // =====================================
    // HELPER
    // =====================================

    const fixValue = (val) => {
      const raw = String(val ?? "").trim();
      return /^\d+$/.test(raw) ? Number(raw) : raw;
    };

    // ✅ NEW: convert a raw value into a real Excel date cell (in-place patch)
    const setDateCell = (ws, r, c, rawVal, style) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (rawVal) {
        try {
          const date = new Date(
            /^\d{4}-\d{2}-\d{2}$/.test(String(rawVal))
              ? `${rawVal}T00:00:00`
              : rawVal
          );
          if (!isNaN(date.getTime())) {
            ws[addr] = { v: date, t: "d", z: "dd-mmm-yyyy", s: style };
            return;
          }
        } catch {}
      }
      ws[addr] = { v: "", t: "s", s: style };
    };

    // =====================================
    // BUILD ROWS
    // =====================================

    const rows = tableData.map((item, index) => {
      const row = {
        "S.No": index + 1,
        "Emp ID": fixValue(item.employee),
        State: item.state_name,
        "Sub State": item.substate,
        "Employee Name": item.employee_name,
        Designation: item.employee_designation,
        HQ: item.headquarter_name,
        Division: item.employee_division,
        DOJ: item.doj ?? item.added_date ?? "",                 // ✅ raw value, patched below
        "Resignation Date": item.resignation_date ?? "",        // ✅ NEW column, raw value, patched below
        Mobile: item.mobile,
        Manager: item.employee_manager,
        "Stockist Code": fixValue(item.stockist_code),
        "Stockist Name": item.stockist_name,
        "Stockist Assigned Date": item.added_date ?? "",         // ✅ raw value, patched below
      };

      selectedMonths.forEach((m) => {
        const label = new Date(selectedYear, m - 1).toLocaleString("default", {
          month: "short", year: "numeric",
        });
        row[`${label} Target`] = Number(item[`target_${m}`] ?? item[`pobtarget_${m}`] ?? 0);
        row[`${label} POB`]    = Number(item[`pob_${m}`] ?? 0);
        row[`${label} SOB`]    = Number(item[`sob_${m}`] ?? 0);
      });

      return row;
    });

    // =====================================
    // TOTAL ROW
    // =====================================

    const totals = calculateMonthlyTotals(tableData, selectedMonths);

    const totalRow = {
      "S.No": "", "Emp ID": "", State: "", "Sub State": "",
      "Employee Name": "TOTAL", Designation: "", HQ: "", Division: "",
      DOJ: "", "Resignation Date": "", Mobile: "", Manager: "", "Stockist Code": "",
      "Stockist Name": "", "Stockist Assigned Date": "",
    };

    totals.forEach((monthData) => {
      const label = new Date(selectedYear, monthData.month - 1).toLocaleString("default", {
        month: "short", year: "numeric",
      });
      totalRow[`${label} Target`] = Number(monthData.target || 0);
      totalRow[`${label} POB`]    = Number(monthData.pob    || 0);
      totalRow[`${label} SOB`]    = Number(monthData.sob    || 0);
    });

    rows.push(totalRow);

    const ws = XLSX.utils.json_to_sheet(rows);

    // =====================================
    // FORCE TYPES (Emp ID + Stockist Code)
    // =====================================

    const range          = XLSX.utils.decode_range(ws["!ref"]);
    const empColIndex    = Object.keys(rows[0]).indexOf("Emp ID");
    const stockistColIndex = Object.keys(rows[0]).indexOf("Stockist Code");

    for (let R = 1; R <= range.e.r; ++R) {
      let cell = ws[XLSX.utils.encode_cell({ r: R, c: empColIndex })];
      if (cell) {
        const raw = String(cell.v ?? "").trim();
        cell.t = /^\d+$/.test(raw) ? "n" : "s";
      }

      cell = ws[XLSX.utils.encode_cell({ r: R, c: stockistColIndex })];
      if (cell) {
        const raw = String(cell.v ?? "").trim();
        cell.t = /^\d+$/.test(raw) ? "n" : "s";
      }
    }

    // =====================================
    // STYLES
    // =====================================

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2563EB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const rowStyle = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    const totalRowStyle = {
      font: { bold: true },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top:    { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left:   { style: "thin", color: { rgb: "000000" } },
        right:  { style: "thin", color: { rgb: "000000" } },
      },
    };

    // =====================================
    // APPLY STYLES TO ALL CELLS
    // =====================================

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });

        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: "z", v: "" };
        }

        if (R === 0) {
          ws[cellAddress].s = headerStyle;         // Header row
        } else if (R === range.e.r) {
          ws[cellAddress].s = totalRowStyle;       // Total row
        } else {
          ws[cellAddress].s = rowStyle;            // Data rows
        }
      }
    }

    // =====================================
    // ✅ PATCH DATE COLUMNS AS REAL EXCEL DATES
    // (DOJ, Resignation Date, Stockist Assigned Date)
    // =====================================

    const dojColIndex        = Object.keys(rows[0]).indexOf("DOJ");
    const resignColIndex     = Object.keys(rows[0]).indexOf("Resignation Date");
    const assignedColIndex   = Object.keys(rows[0]).indexOf("Stockist Assigned Date");

    for (let R = 1; R < range.e.r; ++R) { // exclude total row
      const dataItem = tableData[R - 1];
      if (!dataItem) continue;

      setDateCell(ws, R, dojColIndex, dataItem.doj ?? dataItem.added_date, rowStyle);
      setDateCell(ws, R, resignColIndex, dataItem.resignation_date, rowStyle);
      setDateCell(ws, R, assignedColIndex, dataItem.added_date, rowStyle);
    }

    // Total row: keep these cells blank (already "" from totalRow), restyle to totalRowStyle
    [dojColIndex, resignColIndex, assignedColIndex].forEach((c) => {
      const addr = XLSX.utils.encode_cell({ r: range.e.r, c });
      ws[addr] = { v: "", t: "s", s: totalRowStyle };
    });

    // =====================================
    // COLUMN WIDTHS
    // =====================================

    ws["!cols"] = Object.keys(rows[0]).map(() => ({ wch: 22 }));

    // =====================================
    // FREEZE HEADER
    // =====================================

    ws["!freeze"] = { xSplit: 0, ySplit: 1 };

    // =====================================
    // FILTERS
    // =====================================

    const lastCol = XLSX.utils.encode_col(range.e.c);
    ws["!autofilter"] = { ref: `A1:${lastCol}1` };

    // =====================================
    // EXPORT
    // =====================================

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Quarterly Report");
    XLSX.writeFile(wb, "Stockist_POB_SOB_Report.xlsx");

  } catch (error) {
    console.error(error);
    showErrorToast("Export failed");
  }
};

  const calculateMonthlyTotals = (data = [], months = []) =>
    months.map((month) => ({
      month,
      target: data.reduce(
        (sum, item) => sum + (Number(item[`target_${month}`]) || 0),
        0,
      ),
      pob: data.reduce(
        (sum, item) => sum + (Number(item[`pob_${month}`]) || 0),
        0,
      ),
      sob: data.reduce(
        (sum, item) => sum + (Number(item[`sob_${month}`]) || 0),
        0,
      ),
    }));

  const selectedQuarterObj = quarters.find((q) => q.value === selectedQuarter);
  const selectedMonths = selectedQuarterObj?.months || [];
  const selectedYear = selectedQuarter
    ? parseInt(selectedQuarter.split("-")[1], 10)
    : new Date().getFullYear();

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && <Loader onCancel={() => setIsLoading(false)} />}
      <div className="max-w-full p-4 bg-gray-50 border border-gray-300 rounded-lg shadow-sm">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Stockist</span>{" "}
          <span className="text-blue-500">POB vs SOB</span>
        </h1>

        <div className="grid grid-cols-5 gap-3 items-center mb-3">
          <label className="text-right whitespace-nowrap">State</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            disabled={isLoading}
          >
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="text-right whitespace-nowrap">Division</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
            disabled={isLoading || divisions.length === 0}
          >
            {divisions.map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>

          <div />
        </div>

        <div className="grid grid-cols-5 gap-3 items-center">
          <label className="text-right whitespace-nowrap">Employee</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={isLoading || employees.length === 0}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.id === "All"
                  ? "All"
                  : `${emp.first_name} ${emp.last_name} | ${emp.employee} | ${emp.designation || "-"} | ${emp.division || "-"} | ${emp.state_name || "-"}`}
              </option>
            ))}
          </select>

          <label className="text-right whitespace-nowrap">Month / Year</label>
          <select
            className="w-full p-2 border rounded text-sm"
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(e.target.value)}
            disabled={isLoading || quarters.length === 0}
          >
            {quarters.map((option, idx) => (
              <option key={idx} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div />
        </div>

        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
            className="break-normal"
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>

        {error && (
          <div className="mt-3 text-center text-sm text-red-600">{error}</div>
        )}
      </div>

      {showTable && (
        <div
          ref={tableRef}
          className="mt-4 bg-white border border-gray-300 rounded-lg p-4"
        >
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr className="sticky top-0 z-10 bg-slate-800 text-white">
                  <th className="px-3 py-2 text-left">S.No</th>
                  <th className="px-3 py-2 text-left">Emp ID</th>
                  <th className="px-3 py-2 text-left">State</th>
                  <th className="px-3 py-2 text-left">Sub State </th>
                  <th className="px-3 py-2 text-left">Employee Name</th>
                  <th className="px-3 py-2 text-left">Desi</th>
                  <th className="px-3 py-2 text-left">HQ</th>
                  <th className="px-3 py-2 text-left">Div</th>
                  <th className="px-3 py-2 text-left">Doj</th>
                  <th className="px-3 py-2 text-left">Mobile</th>
                  <th className="px-3 py-2 text-left">Manager</th>
                  <th className="px-3 py-2 text-left">stockist code</th>
                  <th className="px-3 py-2 text-left">stockist name</th>
                  <th className="px-3 py-2 text-left">
                    Stockist Assigned Date
                  </th>

                  {selectedMonths.map((monthNum) => {
                    const label = new Date(
                      selectedYear,
                      monthNum - 1,
                    ).toLocaleString("default", {
                      month: "short",
                      year: "numeric",
                    });
                    return (
                      <React.Fragment key={monthNum}>
                        <th className="px-3 py-2 text-left">{label} Target</th>
                        <th className="px-3 py-2 text-left">{label} POB</th>
                        <th className="px-3 py-2 text-left">{label} SOB</th>
                      </React.Fragment>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {tableData.length === 0 && (
                  <tr>
                    <td
                      colSpan={10 + selectedMonths.length * 3}
                      className="px-3 py-6 text-center text-sm text-gray-600"
                    >
                      No records found for the selected filters.
                    </td>
                  </tr>
                )}

                {tableData.map((item, index) => (
                  <tr
                    key={item.stockist_id}
                    className="odd:bg-white even:bg-slate-50 align-top"
                  >
                    <td className="px-3 py-2 text-sm">{index + 1}</td>
                    <td className="px-3 py-2 text-sm">{item.employee}</td>
                    <td className="px-3 py-2 text-sm">{item.state_name}</td>
                    <td className="px-3 py-2 text-sm">{item.substate}</td>
                    <td className="px-3 py-2 text-sm">{item.employee_name}</td>
                    <td className="px-3 py-2 text-sm">
                      {item.employee_designation}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {item.headquarter_name}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {item.employee_division}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {formatDate(item.doj ?? item.added_date)}
                    </td>
                    <td className="px-3 py-2 text-sm">{item.mobile}</td>
                    <td className="px-3 py-2 text-sm">
                      {item.employee_manager}
                    </td>
                    <td className="px-3 py-2 text-sm">{item.stockist_code}</td>
                    <td className="px-3 py-2 text-sm">{item.stockist_name}</td>
                    <td className="px-3 py-2 text-sm">
                      {formatDate(item.added_date, "dd MMM yyyy")}
                    </td>

                    {selectedMonths.map((month) => (
                      <React.Fragment key={month}>
                        <td className="px-3 py-2 text-sm">
                          {formatNumber(
                            item[`target_${month}`] ??
                            item[`pobtarget_${month}`] ??
                            0,
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {formatNumber(item[`pob_${month}`] ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          {formatNumber(item[`sob_${month}`] ?? 0)}
                        </td>
                      </React.Fragment>
                    ))}
                  </tr>
                ))}

                {tableData.length > 0 && (
                  <tr>
                    <td colSpan={14} className="bg-blue-600 px-3 py-2" />
                    {calculateMonthlyTotals(tableData, selectedMonths).map(
                      (monthData) => (
                        <React.Fragment key={monthData.month}>
                          <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                            {formatNumber(monthData.target)}
                          </td>
                          <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                            {formatNumber(monthData.pob)}
                          </td>
                          <td className="px-3 py-2 font-semibold text-white bg-blue-600">
                            {formatNumber(monthData.sob)}
                          </td>
                        </React.Fragment>
                      ),
                    )}
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-4 justify-center">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={handleExport}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockistPOBSOBTargetQuarterly;
